import { useTranslate } from '@tolgee/react';
import React from 'react';

import { LomavisUpgradeSubscriptionButton1 } from '@/lomavisSharedUi/atoms/links/LomavisUpgradeSubscriptionButton1';

/** The three steps of the overlay workflow, in the order a user meets them. */
const WORKFLOW_STEPS = ['design', 'organize', 'apply'] as const;

/**
 * Reading width of the explainer column.
 *
 * The white surface fills the page, but the copy must not: stretched across a
 * wide monitor the lines run too long to read, and the same text in three
 * side-by-side columns turns into slivers. One centred column keeps the line
 * length the same on a laptop and on a 34" screen.
 */
const CONTENT_MAX_WIDTH = 720;

/**
 * What an account without the overlay feature sees in place of the library.
 *
 * It has to answer "what is this and what would I do with it" before it asks
 * for an upgrade, so the page explains the feature rather than only reporting
 * that it is missing.
 */
export const OverlayTemplatesUpsell: React.FC = () => {
  const { t } = useTranslate();

  return (
    <div className="d-flex flex-column flex-grow-1 h-100 p-5 overflow-auto">
      <div className="card bg-white rounded d-flex flex-column flex-grow-1 justify-content-center align-items-center p-5">
        <div
          className="d-flex flex-column w-100"
          style={{ maxWidth: CONTENT_MAX_WIDTH }}
        >
          <div className="d-flex flex-column align-items-center text-center mb-8">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-light-primary text-primary mb-4 fs-2x"
              style={{ width: 64, height: 64 }}
            >
              <i className="fa-duotone fa-layer-group" aria-hidden="true"></i>
            </div>
            <h2 className="fs-2 fw-bold text-gray-900 mb-3">
              {t('overlayTemplates.upsell.title', 'Overlays')}
            </h2>
            <p className="fs-5 text-gray-700 mb-0">
              {t(
                'overlayTemplates.upsell.description',
                'An overlay is a reusable design layer — your logo, a headline, a price badge, a frame — that you build once and then lay over any image or video before you publish it.'
              )}
            </p>
          </div>

          <div className="d-flex flex-column gap-4 mb-8">
            {WORKFLOW_STEPS.map((step, index) => (
              <div className="d-flex flex-row align-items-start gap-4" key={step}>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold flex-shrink-0 fs-5"
                  style={{ width: 40, height: 40 }}
                >
                  {index + 1}
                </div>
                <div className="d-flex flex-column">
                  <div className="fs-5 fw-semibold text-gray-900 mb-1">
                    {t(`overlayTemplates.upsell.${step}Title`)}
                  </div>
                  <div className="fs-6 text-gray-700">
                    {t(`overlayTemplates.upsell.${step}Description`)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex flex-column align-items-center gap-4 text-center">
            <div className="fs-5 fw-semibold text-gray-800">
              {t(
                'overlayTemplates.notAvailable',
                'Overlays are not included in your current subscription.'
              )}
            </div>
            <LomavisUpgradeSubscriptionButton1 />
          </div>
        </div>
      </div>
    </div>
  );
};
